import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ImageModal from './ImageModal';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(45deg, #ff6b6b 0%, #4ecdc4 25%, #45b7d1 50%, #96ceb4 75%, #ffeaa7 100%);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Header = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ff6b6b;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 12px 20px;
  color: #000000;
  text-decoration: none;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    background: #96ceb4;
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0px #000000;
  }
`;

const UserInfo = styled.div`
  text-align: center;
  color: #000000;
`;

const UserTitle = styled.h1`
  font-size: 2rem;
  font-weight: 900;
  margin-bottom: 10px;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 4px 4px 0px #ffffff;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const UserSubtitle = styled.p`
  font-size: 1.1rem;
  color: #000000;
  margin-bottom: 40px;
  font-weight: 700;
  background: #ffeaa7;
  padding: 8px 16px;
  border: 3px solid #000000;
  transform: rotate(-1deg);
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatCard = styled.div`
  background: #ffffff;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 20px;
  text-align: center;
  min-width: 120px;
  box-shadow: 6px 6px 0px #000000;
  transform: rotate(1deg);
  
  &:hover {
    transform: rotate(-1deg) translate(-2px, -2px);
    box-shadow: 8px 8px 0px #000000;
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #000000;
  margin-bottom: 5px;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const StatLabel = styled.div`
  color: #000000;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const WorksContainer = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const SectionTitle = styled.h2`
  color: #000000;
  font-size: 1.5rem;
  font-weight: 900;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 3px 3px 0px #ffffff;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const WorksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const WorkCard = styled.div`
  background: #ffffff;
  border: 4px solid #000000;
  border-radius: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  box-shadow: 6px 6px 0px #000000;
  transform: rotate(1deg);
  
  &:hover {
    transform: rotate(-1deg) translate(-3px, -3px);
    box-shadow: 9px 9px 0px #000000;
  }
`;

const WorkImagesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  position: relative;
`;

const ImageCount = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 1;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const SingleWorkImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 12px 12px 0 0;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const WorkContent = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const WorkPrompt = styled.div`
  color: #000000;
  font-size: 0.9rem;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 600;
`;

const WorkMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000000;
  font-size: 0.8rem;
  margin-bottom: 10px;
  font-weight: 600;
`;

const WorkStatus = styled.span`
  padding: 6px 12px;
  border-radius: 0;
  font-size: 0.7rem;
  font-weight: 900;
  border: 2px solid #000000;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#96ceb4';
      case 'processing': return '#45b7d1';
      case 'failed': return '#ff6b6b';
      default: return '#ffeaa7';
    }
  }};
  color: #000000;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #000000;
  font-weight: 700;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const EmptyText = styled.div`
  font-size: 1.2rem;
  margin-bottom: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const EmptySubtext = styled.div`
  font-size: 1rem;
  font-weight: 700;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  color: #000000;
  font-size: 1.1rem;
  font-weight: 700;
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #f0f0f0 75%), 
              linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  font-weight: 600;
  border-bottom: 2px solid #ddd;
`;

const RefreshButton = styled.button`
  background: #4ecdc4;
  border: 3px solid #000000;
  border-radius: 0;
  padding: 8px 16px;
  color: #000000;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.8rem;
  margin-top: 8px;
  box-shadow: 3px 3px 0px #000000;
  
  &:hover {
    background: #45b7d1;
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0px #000000;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: 2px 2px 0px #000000;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  border: 2px solid #ff6b6b;
  border-radius: 0;
  padding: 12px;
  margin: 8px 0;
  color: #c62828;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  background: ${props => props.active ? '#ff6b6b' : '#ffffff'};
  border: 3px solid #000000;
  border-radius: 0;
  padding: 10px 15px;
  color: #000000;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  box-shadow: 3px 3px 0px #000000;
  min-width: 45px;
  
  &:hover {
    background: ${props => props.active ? '#ff6b6b' : '#4ecdc4'};
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0px #000000;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: 2px 2px 0px #000000;
  }
`;

const PaginationInfo = styled.div`
  color: #000000;
  font-weight: 700;
  font-size: 0.9rem;
  background: #ffeaa7;
  padding: 8px 12px;
  border: 2px solid #000000;
  margin: 0 10px;
`;



function UserCenter() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalWorks: 0,
    completedWorks: 0,
    todayWorks: 0
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [refreshingTasks, setRefreshingTasks] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const worksPerPage = 12;

  useEffect(() => {
    fetchUserWorks();
  }, [userId]);

  // 当作品数据变化时，重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [works.length]);



  const fetchUserWorks = async () => {
    try {
      setLoading(true);
      console.log('正在获取用户作品...');
      const response = await axios.get('/api/history');
      console.log('API响应:', response.data);
      
      if (response.data.success) {
        const worksData = response.data.data;
        setWorks(worksData);
        
        // 计算统计数据
        const today = new Date().toISOString().split('T')[0];
        const completedCount = worksData.filter(work => work.status === 'completed').length;
        const todayCount = worksData.filter(work => 
          work.created_at.startsWith(today)
        ).length;
        
        setStats({
          totalWorks: worksData.length,
          completedWorks: completedCount,
          todayWorks: todayCount
        });
      } else {
        console.error('API返回失败:', response.data.message);
        alert('获取作品失败: ' + response.data.message);
      }
    } catch (error) {
      console.error('获取用户作品失败:', error);
      if (error.response) {
        console.error('错误响应:', error.response.data);
        alert('获取作品失败: ' + (error.response.data.message || error.message));
      } else {
        alert('网络错误，请检查连接');
      }
    } finally {
      setLoading(false);
    }
  };



  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // 转换为北京时间 (UTC+8)
    const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000));
    return beijingTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Shanghai'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return '已完成';
      case 'processing': return '生成中';
      case 'pending': return '等待中';
      case 'failed': return '失败';
      default: return '未知';
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleImageClick = (imageUrl, images, index, prompt) => {
    setSelectedImage(imageUrl);
    setSelectedImages(images);
    setCurrentImageIndex(index);
    setCurrentPrompt(prompt);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedImages([]);
    setCurrentImageIndex(0);
    setCurrentPrompt('');
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(selectedImages[newIndex]);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < selectedImages.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(selectedImages[newIndex]);
    }
  };

  // 分页逻辑
  const totalPages = Math.ceil(works.length / worksPerPage);
  const startIndex = (currentPage - 1) * worksPerPage;
  const endIndex = startIndex + worksPerPage;
  const currentWorks = works.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // 上一页按钮
    pages.push(
      <PaginationButton
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </PaginationButton>
    );

    // 第一页
    if (startPage > 1) {
      pages.push(
        <PaginationButton
          key={1}
          onClick={() => handlePageChange(1)}
          active={currentPage === 1}
        >
          1
        </PaginationButton>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1">...</span>);
      }
    }

    // 页码按钮
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationButton
          key={i}
          onClick={() => handlePageChange(i)}
          active={currentPage === i}
        >
          {i}
        </PaginationButton>
      );
    }

    // 最后一页
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2">...</span>);
      }
      pages.push(
        <PaginationButton
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          active={currentPage === totalPages}
        >
          {totalPages}
        </PaginationButton>
      );
    }

    // 下一页按钮
    pages.push(
      <PaginationButton
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </PaginationButton>
    );

    return (
      <PaginationContainer>
        {pages}
        <PaginationInfo>
          第 {currentPage} 页 / 共 {totalPages} 页 (共 {works.length} 个作品)
        </PaginationInfo>
      </PaginationContainer>
    );
  };

  const refreshTaskStatus = async (taskId) => {
    // 防止重复刷新
    if (refreshingTasks.has(taskId)) {
      alert('该任务正在获取中，请稍候...');
      return;
    }

    setRefreshingTasks(prev => new Set([...prev, taskId]));

    try {
      const response = await axios.post(`/api/fetch-task-status/${taskId}`);
      
      if (response.data.success) {
        alert('任务状态已更新！');
        // 重新获取用户作品列表
        await fetchUserWorks();
      } else {
        alert('获取失败：' + (response.data.message || '未知错误'));
      }
    } catch (error) {
      console.error('获取任务状态失败:', error);
      alert('网络错误，请稍后重试');
    } finally {
      setRefreshingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack}>
          ← 返回创作空间
        </BackButton>
        <UserInfo>
          <UserTitle>👑 {user?.username || '尊贵会员'} 的创作中心</UserTitle>
          <UserSubtitle>专属艺术作品展示空间</UserSubtitle>
        </UserInfo>
        <div></div>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatNumber>{stats.totalWorks}</StatNumber>
          <StatLabel>总作品数</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.completedWorks}</StatNumber>
          <StatLabel>已完成</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.todayWorks}</StatNumber>
          <StatLabel>今日创作</StatLabel>
        </StatCard>
      </StatsContainer>

      <WorksContainer>
        <SectionTitle>
          <span>🎨</span>
          创作作品集

        </SectionTitle>
        
        {loading ? (
          <LoadingContainer>
            ⏳ 正在加载作品...
          </LoadingContainer>
        ) : works.length > 0 ? (
          <>
            <WorksGrid>
              {currentWorks.map((work) => {
              const hasImages = work.result_images && work.result_images.length > 0;
              const isCompleted = work.status === 'completed';
              const isFailed = work.status === 'failed';
              const isProcessing = work.status === 'processing';
              const isPending = work.status === 'pending';
              const needsRefresh = isPending || isProcessing || (isCompleted && !hasImages) || isFailed;
              
              return (
                <WorkCard key={work.id}>
                  {hasImages ? (
                    work.result_images.length === 1 ? (
                       <SingleWorkImage 
                         src={work.result_images[0]} 
                         alt="创作作品"
                         onClick={() => handleImageClick(work.result_images[0], work.result_images, 0, work.prompt)}
                         onError={(e) => {
                           e.target.src = 'https://picsum.photos/300/200?random=' + work.id;
                         }}
                       />
                     ) : (
                       <WorkImagesContainer>
                         <ImageCount>{work.result_images.length} 张</ImageCount>
                         {work.result_images.slice(0, 4).map((image, index) => (
                            <WorkImage 
                              key={index}
                              src={image} 
                              alt={`创作作品 ${index + 1}`}
                              onClick={() => handleImageClick(image, work.result_images, index, work.prompt)}
                              onError={(e) => {
                                e.target.src = 'https://picsum.photos/150/100?random=' + work.id + index;
                              }}
                            />
                          ))}
                       </WorkImagesContainer>
                     )
                  ) : (
                    <NoImagePlaceholder>
                      {isFailed ? (
                        <>
                          <div style={{fontSize: '2rem', marginBottom: '8px'}}>❌</div>
                          <div>生成失败</div>
                        </>
                      ) : isCompleted ? (
                        <>
                          <div style={{fontSize: '2rem', marginBottom: '8px'}}>📷</div>
                          <div>暂无图片</div>
                        </>
                      ) : (
                        <>
                          <div style={{fontSize: '2rem', marginBottom: '8px'}}>⏳</div>
                          <div>生成中...</div>
                        </>
                      )}
                    </NoImagePlaceholder>
                  )}
                  
                  <WorkContent>
                    <WorkPrompt>{work.prompt}</WorkPrompt>
                    
                    {isFailed && work.error_message && (
                      <ErrorMessage>
                        <strong>错误原因：</strong><br/>
                        {work.error_message}
                      </ErrorMessage>
                    )}
                    
                    <WorkMeta>
                      <span>{work.aspect_ratio}</span>
                      <WorkStatus status={work.status}>
                        {getStatusText(work.status)}
                      </WorkStatus>
                    </WorkMeta>
                    
                    <WorkMeta>
                      <span>创作时间: {formatDate(work.created_at)}</span>
                    </WorkMeta>
                    
                    {work.updated_at && work.updated_at !== work.created_at && (
                      <WorkMeta>
                        <span>完成时间: {formatDate(work.updated_at)}</span>
                      </WorkMeta>
                    )}
                    
                    {needsRefresh && (
                      <ActionButtons>
                        <RefreshButton 
                          onClick={() => refreshTaskStatus(work.id)}
                          disabled={refreshingTasks.has(work.id)}
                        >
                          {refreshingTasks.has(work.id) ? '获取中...' : 
                           isPending ? '手动获取' :
                           isProcessing ? '手动获取' :
                           isFailed ? '重新获取' : '获取图片'}
                        </RefreshButton>
                      </ActionButtons>
                    )}
                  </WorkContent>
                </WorkCard>
               );
             })}
            </WorksGrid>
            {renderPagination()}
          </>
        ) : (
          <EmptyState>
            <EmptyIcon>✨</EmptyIcon>
            <EmptyText>还没有创作记录</EmptyText>
            <EmptySubtext>开始您的专属艺术创作之旅吧！</EmptySubtext>
          </EmptyState>
        )}
      </WorksContainer>
      
      <ImageModal 
        isOpen={isModalOpen}
        imageUrl={selectedImage}
        images={selectedImages}
        currentIndex={currentImageIndex}
        onClose={handleCloseModal}
        onPrevious={selectedImages.length > 1 ? handlePreviousImage : null}
        onNext={selectedImages.length > 1 ? handleNextImage : null}
        title={`第 ${currentImageIndex + 1} 张 / 共 ${selectedImages.length} 张`}
        description={currentPrompt}
      />
    </Container>
  );
}

export default UserCenter;